import {defineStore} from 'pinia'
import {type SystemNs} from "@shared/types/system.namespace";
import {AccountNs, CommonNs} from "@shared/types";
import {AccountApi, ImageApi, LoginActivityApi, SystemApi} from "@/api";
import {useWorkspaceStore} from "@shared/stores/workspace.store";


export const useAccountStore = defineStore("account", {
    state: () => ({
        account: {} as AccountNs.Account,
        generalInfo: {} as AccountNs.GeneralInfo,
        timeZones: {} as SystemNs.TimeZone[],
        loginActivities: {} as AccountNs.LoginActivity[]
    }),
    getters: {
        getAccount: (state) => state.account,
        getProfileCover: (state) => state.account.profileCover,
        getGeneralInfo: (state) => state.generalInfo,
        getTimeZones: (state) => state.timeZones,
        getLoginActivities: (state) => state.loginActivities,
    },
    actions: {
        async loadAccount(): Promise<AccountNs.Account> {
            return AccountApi.getAccount()
                .then((account: AccountNs.Account) => {
                    this.account = account;
                    return account
                })
        },

        async updateProperties(
            payload: AccountNs.UpdateAccountPropertyRequest
        ): Promise<AccountNs.Account> {
            return AccountApi.updateProperties(payload)
                .then((account: AccountNs.Account) => {
                    this.account = account;
                    return account
                })
        },

        async deleteAccount(
            payload: AccountNs.DeleteAccountRequest
        ): Promise<CommonNs.AnyResponse> {
            return AccountApi.deleteAccount(payload)
                .then((response) => response)
        },

        async confirmDeleteAccount(payload: AccountNs.ConfirmDeleteAccountRequest
        ): Promise<CommonNs.AnyResponse> {
            return AccountApi.confirmDeleteAccount(payload)
                .then((response) => response)
        },

        async uploadProfileCover(payload: FormData): Promise<CommonNs.AnyResponse> {
            const wsStore = useWorkspaceStore()
            const workspaceId = wsStore.getSelectedWorkspaceId
            payload.append('workspaceId', workspaceId)
            payload.append('bucketType', CommonNs.ImageBucketEnums.USER_COVER_BUCKET)
            return ImageApi.uploadImage(payload).then((response) => response)
        },

        async removeProfileCover(payload: string): Promise<CommonNs.AnyResponse> {
            const wsStore = useWorkspaceStore()
            const workspaceId = wsStore.getSelectedWorkspaceId
            return ImageApi.removeImage(workspaceId, CommonNs.ImageBucketEnums.USER_COVER_BUCKET, payload)
                .then((response) => response)
        },

        async uploadProfileAvatar(payload: FormData): Promise<CommonNs.AnyResponse> {
            const wsStore = useWorkspaceStore()
            const workspaceId = wsStore.getSelectedWorkspaceId
            payload.append('workspaceId', workspaceId)
            payload.append('bucketType', CommonNs.ImageBucketEnums.USER_IMAGE_BUCKET)
            return ImageApi.uploadImage(payload).then((response) => response)
        },

        async removeProfileAvatar(payload: string): Promise<CommonNs.AnyResponse> {
            const wsStore = useWorkspaceStore()
            const workspaceId = wsStore.getSelectedWorkspaceId
            return ImageApi.removeImage(workspaceId, CommonNs.ImageBucketEnums.USER_IMAGE_BUCKET, payload)
                .then((response) => response)
        },

        async changePrimaryEmail(payload: AccountNs.ChangePrimaryEmailRequest): Promise<AccountNs.ChangePrimaryEmailRequest> {
            return AccountApi.changePrimaryEmail(payload).then((response) => response)
        },

        async changePassword(payload: AccountNs.ChangePasswordRequest): Promise<AccountNs.ChangePasswordRequest> {
            return AccountApi.changePassword(payload).then((response) => response)
        },

        async loadGeneralInfo(): Promise<AccountNs.GeneralInfo> {
            return AccountApi.getGeneralInfo()
                .then((response) => {
                    this.generalInfo = response;
                    return response
                })
        },

        async updateGeneralInfo(payload: AccountNs.UpdateGeneralInfoRequest): Promise<AccountNs.GeneralInfo> {
            return AccountApi.updateGeneralInfo(payload)
                .then((response) => {
                    this.generalInfo = response;
                    return response
                })
        },

        async loadUserLoginActivities(): Promise<AccountNs.LoginActivity[]> {
            return LoginActivityApi.getLoginActivities().then((response) => {
                this.loginActivities = response;
                return response
            })
        },

        async logoutFromDevice(payload: string): Promise<AccountNs.LoginActivity[]> {
            return LoginActivityApi.logoutFromDevice(payload).then((response) => {
                this.loginActivities = response;
                return response
            })
        },

        async logoutFromAllDevices(): Promise<AccountNs.LoginActivity[]> {
            return LoginActivityApi.logoutFromAllDevices().then((response) => {
                this.loginActivities = response;
                return response
            })
        },

        async loadTimeZones(): Promise<SystemNs.TimeZone[]> {
            return SystemApi.getAllTimeZones()
                .then((response) => {
                    this.timeZones = response;
                    return response
                })
        },

    }
})
